import { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  AggregateEuRootRequest,
  useAggregateEuRoot,
} from 'src/apis/aggregate-eu/use-aggregate-eu-root';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { AggregateEuCentralBuyerListCreate } from 'src/pages/aggregate-eu/central-buyer/create';
import { AggregateEuCentralBuyer } from 'src/pages/aggregate-eu/central-buyer/overview';
import { AggregateEuContractReports } from 'src/pages/aggregate-eu/contract-reports';
import { AggregateEuCreateContractReport } from 'src/pages/aggregate-eu/contract-reports/create';
import { AggregateEuContractReportDetails } from 'src/pages/aggregate-eu/contract-reports/details';
import { AggregateEuEditContractReport } from 'src/pages/aggregate-eu/contract-reports/edit';
import AggregateEuDemandsPage from 'src/pages/aggregate-eu/demands';
import { AggregateEuGuest } from 'src/pages/aggregate-eu/guest';
import { AggregateEuOverview } from 'src/pages/aggregate-eu/overview';
import { AggregateEuServiceSettingsPage } from 'src/pages/aggregate-eu/service-settings';
import { AggregateEuTenderingAndMatching } from 'src/pages/aggregate-eu/tendering-and-matching';
import { AggregateEuBuyerMatchDetails } from 'src/pages/aggregate-eu/tendering-and-matching/my-buyer-matches/details';
import { AggregateEuSellerMatchDetails } from 'src/pages/aggregate-eu/tendering-and-matching/my-seller-matches/details';

export const AggregateEu = () => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const cognitoUser = useCognitoUser();
  const isAuthenticated = Boolean(monolithUser || cognitoUser.data);

  return (
    <Switch>
      <Route path="/aggregate-eu" exact>
        <Redirect to="/aggregate-eu/overview" />
      </Route>
      {!isAuthenticated && (
        <Route path="/aggregate-eu/overview">
          <AggregateEuGuest />
        </Route>
      )}
      <Route path="/aggregate-eu/overview">
        <AuthenticatedRoute component={AggregateEuOverview} />
      </Route>
      <Route path="/aggregate-eu/demands">
        <AuthenticatedRoute component={AggregateEuDemandsPage} />
      </Route>
      <Route path="/aggregate-eu/tendering-and-matching/my-buyer-matches/:buyerMatchId">
        <AuthenticatedRoute component={AggregateEuBuyerMatchDetails} />
      </Route>
      <Route path="/aggregate-eu/tendering-and-matching/my-seller-matches/:sellerMatchId">
        <AuthenticatedRoute component={AggregateEuSellerMatchDetails} />
      </Route>
      <Route path="/aggregate-eu/tendering-and-matching">
        <AuthenticatedRoute component={AggregateEuTenderingAndMatching} />
      </Route>
      <Route path="/aggregate-eu/contract-reports/create">
        <AuthenticatedRoute component={AggregateEuCreateContractReport} />
      </Route>
      <Route path="/aggregate-eu/contract-reports/:contractReportId/edit">
        <AuthenticatedRoute component={AggregateEuEditContractReport} />
      </Route>
      <Route path="/aggregate-eu/contract-reports/:contractReportId" exact>
        <AuthenticatedRoute component={AggregateEuContractReportDetails} />
      </Route>
      <Route path="/aggregate-eu/contract-reports">
        <AuthenticatedRoute component={AggregateEuContractReports} />
      </Route>
      <Route path="/aggregate-eu/central-buyer/create" exact>
        <AuthenticatedRoute component={AggregateEuCentralBuyerListCreate} />
      </Route>
      <Route path="/aggregate-eu/central-buyer" exact>
        <AuthenticatedRoute component={AggregateEuCentralBuyer} />
      </Route>
      <Route path="/aggregate-eu/service-settings">
        <AuthenticatedRoute component={AggregateEuServiceSettingsPage} />
      </Route>
      <Route>
        <ThrowNotFound />
      </Route>
    </Switch>
  );
};

/** Wrapper for AggregateEU routes that need authentication for access.
 * We explictely want to get a 401 when these routes are called without authentication. */
const AuthenticatedRoute: FC<{
  component: FC<{ aggregateEuRootRequest: Successful<AggregateEuRootRequest> }>;
}> = ({ component }) => {
  const aggregateEuRootRequest = useAggregateEuRoot();
  if (!aggregateEuRootRequest?.response) {
    return <PageSpinner />;
  }
  const Page = component;
  return <Page aggregateEuRootRequest={aggregateEuRootRequest} />;
};
